/**
 * Format sport key to human-readable string
 * e.g., "american_football" -> "American Football"
 */
export function formatSport(sport: string): string {
  return sport.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Format ISO date string to human-readable date/time
 * e.g., "2025-01-26T14:30:00Z" -> "Sunday, Jan 26, 2:30 PM EST"
 */
export function formatDateTime(isoString: string): string {
  return new Date(isoString).toLocaleString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

/**
 * Format ISO date string to relative time ago
 * e.g., "5m ago", "2h ago", "1d ago"
 */
export function formatTimeAgo(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
}

/**
 * Format signal type to human-readable string
 * e.g., "sharp_divergence" -> "Sharp Divergence"
 */
export function formatSignalType(type: string): string {
  return type
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Format lifecycle stage to human-readable label
 */
export function formatStage(stage: string | undefined): string {
  if (!stage) return 'Unknown';
  const labels: Record<string, string> = {
    'scheduled': 'Scheduled',
    'pre_game': 'Pre-Game',
    'live': 'Live',
    'completed': 'Completed',
  };
  return labels[stage] || stage;
}

/**
 * Format polling interval in seconds to human-readable string
 * e.g., 30 -> "30s", 120 -> "2 min"
 */
export function formatPollInterval(seconds: number): string {
  if (seconds === 0) return 'No polling';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes} min`;
}

/**
 * Format bet type to human-readable label
 * e.g., "moneyline" -> "H2H / Moneyline"
 */
export function formatBetType(betType: string | null | undefined): string {
  if (!betType) return 'Unknown';
  const labels: Record<string, string> = {
    'moneyline': 'H2H / Moneyline',
    'spread': 'Point Spread',
    'total': 'Over/Under',
    'player_points': 'Player Points',
    'player_rebounds': 'Player Rebounds',
    'player_assists': 'Player Assists',
    'player_pass_tds': 'Passing TDs',
    'player_rush_yds': 'Rushing Yards',
    'player_rec_yds': 'Receiving Yards',
    'player_strikeouts': 'Strikeouts',
    'player_hits': 'Hits',
    'unknown': 'Other',
  };
  return labels[betType] || betType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}
